import { useState } from "react";
import { Alert, Button, ConfirmDialog, useToast } from "@a2z/ui";
import type { AdminAreaSuggestionView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useApproveAreaSuggestionMutation } from "../services/useApproveAreaSuggestionMutation";
import { useRejectAreaSuggestionMutation } from "../services/useRejectAreaSuggestionMutation";
import { AreaFormDialog } from "./AreaFormDialog";

type Props = {
  suggestion: AdminAreaSuggestionView;
};

// Approve/reject actions for a single pending suggestion. Approve opens the
// existing AreaFormDialog pre-filled from the raw name (governorate fixed);
// the dialog itself calls the approve endpoint via `onApprove` below, so a
// human can correct spelling before it becomes canonical reference data.
// Reject asks for confirmation, then calls the reject endpoint directly.
export function AreaSuggestionActions({ suggestion }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const approveMutation = useApproveAreaSuggestionMutation();
  const rejectMutation = useRejectAreaSuggestionMutation();
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const governorateName =
    suggestion.governorateNameAr && suggestion.governorateNameAr.length > 0
      ? suggestion.governorateNameAr
      : (suggestion.governorateNameEn ?? suggestion.governorateId);

  const onApprove = async (nameAr: string, nameEn: string) => {
    try {
      await approveMutation.mutateAsync({ id: suggestion.id, nameAr, nameEn });
      toast.success(t(keys.areaSuggestions.approveSuccess));
    } catch (err) {
      if (err instanceof ApiJsonClientError) {
        if (err.status === 400) {
          throw new ApiJsonClientError({
            message: t(keys.areaSuggestions.alreadyReviewedError),
            status: err.status,
          });
        }
        if (err.status === 409) {
          throw new ApiJsonClientError({
            message: t(keys.areaSuggestions.duplicateNameError),
            status: err.status,
          });
        }
      }
      throw err;
    }
  };

  const onReject = async () => {
    setRejectError(null);
    try {
      await rejectMutation.mutateAsync(suggestion.id);
      toast.success(t(keys.areaSuggestions.rejectSuccess));
      setIsRejectConfirmOpen(false);
    } catch (err) {
      setRejectError(
        err instanceof ApiJsonClientError && err.status === 400
          ? t(keys.areaSuggestions.alreadyReviewedError)
          : err instanceof ApiJsonClientError
            ? err.message || t(keys.errors.generic)
            : t(keys.errors.generic),
      );
    }
  };

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsRejectConfirmOpen(true)}
          className="h-11 rounded-md px-3 text-[13px] font-semibold sm:h-8 sm:px-2.5"
        >
          {t(keys.areaSuggestions.rejectAction)}
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => setIsApproveOpen(true)}
          className="h-11 rounded-md px-3 text-[13px] font-semibold sm:h-8 sm:px-2.5"
        >
          {t(keys.areaSuggestions.approveAction)}
        </Button>
      </div>
      {rejectError ? <Alert message={rejectError} /> : null}

      {isApproveOpen ? (
        <AreaFormDialog
          isOpen
          onClose={() => setIsApproveOpen(false)}
          approveSuggestion={{
            governorateId: suggestion.governorateId,
            governorateName,
            initialNameAr: suggestion.rawName,
            onApprove,
          }}
        />
      ) : null}

      <ConfirmDialog
        isOpen={isRejectConfirmOpen}
        title={t(keys.areaSuggestions.rejectAction)}
        body={t(keys.areaSuggestions.rejectConfirm)}
        confirmLabel={t(keys.areaSuggestions.rejectAction)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={rejectMutation.isPending}
        onConfirm={onReject}
        onCancel={() => setIsRejectConfirmOpen(false)}
      />
    </div>
  );
}
